import React, { PropsWithChildren, Suspense } from 'react';
import { IntlProvider } from 'react-intl';
import { BrowserRouter } from 'react-router-dom';
import AppProvider from '@context/AppProvider';
import AuthGate from '@context/AuthGate';
import ErrorProvider from '@context/ErrorProvider';
import ThemeProvider from '@context/ThemeProvider';
import {
  createQueryCache,
  createShouldRetryQuery,
  getRetryDelay,
  shouldRetryMutation,
} from '@services/queryClient/queryClient';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Locale } from '@constants/common';
import { getLocale } from '@utils/common';

const ISLAND_QUERY_RETRIES = 1;

const queryClient = new QueryClient({
  queryCache: createQueryCache(),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: createShouldRetryQuery(ISLAND_QUERY_RETRIES),
    },
    mutations: { retry: shouldRetryMutation, retryDelay: getRetryDelay },
  },
});

const IslandProviders: React.FC<PropsWithChildren> = ({ children }) => (
  <QueryClientProvider client={queryClient}>
    <IntlProvider
      messages={window.intlMessages}
      locale={getLocale()}
      defaultLocale={Locale.ENGLISH}>
      <AppProvider>
        <ErrorProvider>
          <ThemeProvider>
            <Suspense fallback={null}>
              <BrowserRouter>
                <AuthGate>{children}</AuthGate>
              </BrowserRouter>
            </Suspense>
          </ThemeProvider>
        </ErrorProvider>
      </AppProvider>
    </IntlProvider>
  </QueryClientProvider>
);

export default IslandProviders;
